import React, { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const STAGE_META = [
  { id: 1, label: "Welcome" },
  { id: 2, label: "Preview" },
  { id: 3, label: "Pricing" },
  { id: 4, label: "Marketplace" },
  { id: 5, label: "Subscribe" },
];

export default function ConciergeStageShell({
  stage,
  setStage,
  canAdvance = true,
  onFinish,
  children,
}) {
  useEffect(() => {
    const main = document.querySelector("main");
    const html = document.documentElement;
    const body = document.body;
    const saved = {
      html: html.style.overflow,
      body: body.style.overflow,
      main: main?.style.overflow,
      mainHeight: main?.style.height,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (main) {
      main.style.overflow = "hidden";
      main.style.height = "100dvh";
    }
    return () => {
      html.style.overflow = saved.html;
      body.style.overflow = saved.body;
      if (main) {
        main.style.overflow = saved.main || "";
        main.style.height = saved.mainHeight || "";
      }
    };
  }, []);

  const next = () => {
    if (!canAdvance) return;
    if (stage >= 5) {
      onFinish?.();
      return;
    }
    setStage(stage + 1);
  };
  const prev = () => {
    if (stage <= 1) return;
    setStage(stage - 1);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, canAdvance]);

  const currentLabel = useMemo(
    () => STAGE_META.find((s) => s.id === stage)?.label || "",
    [stage]
  );

  return (
    <div
      className="relative w-full h-[100dvh] overflow-hidden bg-[#FAFAFA]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Ambient tint layer */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 600px at 85% 0%, rgba(239,73,73,0.06), transparent 60%), radial-gradient(900px 500px at 0% 100%, rgba(17,17,17,0.04), transparent 60%)",
        }}
      />

      <div className="relative h-full grid grid-rows-[auto_minmax(0,1fr)_auto]">
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 laptop:px-10 pt-5 laptop:pt-6">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#888888]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
              Bellhopt Concierge
            </span>
            <span className="hidden laptop:inline text-[11px] text-[#888888]">/</span>
            <span className="hidden laptop:inline text-[11px] font-semibold text-[#5F6368]">
              {currentLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              disabled={stage <= 1}
              aria-label="Previous stage"
              className="w-10 h-10 rounded-full bg-white border border-[#EAEAEA] text-[#111111] hover:bg-[#F7F7F8] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <ArrowLeft size={15} strokeWidth={2} />
            </button>
            <button
              onClick={next}
              disabled={!canAdvance}
              aria-label="Next stage"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#111111] text-white text-[12px] font-bold hover:bg-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              {stage >= 5 ? "Subscribe" : "Continue"}
              <ArrowRight size={13} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Stage content */}
        <div className="relative min-h-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
              className="absolute inset-0"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer — dots */}
        <div className="flex items-center justify-center gap-1.5 pb-5 laptop:pb-6">
          {STAGE_META.map((s) => {
            const active = s.id === stage;
            const done = s.id < stage;
            return (
              <button
                key={s.id}
                onClick={() => setStage(s.id)}
                aria-label={`Go to stage ${s.id} — ${s.label}`}
                className="group relative h-2 rounded-full transition-all"
                style={{
                  width: active ? 28 : 8,
                  backgroundColor: active ? "#111111" : done ? "#888888" : "#EAEAEA",
                }}
              >
                <span className="sr-only">{s.label}</span>
                <span className="opacity-0 group-hover:opacity-100 absolute -top-7 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.22em] text-[#888888] whitespace-nowrap transition-opacity hidden laptop:inline">
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

ConciergeStageShell.STAGE_META = STAGE_META;
